import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const withScheme = (u) => (u && !/^[a-z][a-z0-9+.-]*:/i.test(u) ? `https://${u}` : u);
import { inputCls, btnPrimary, Field } from "@/components/shared";

const EMPTY = { name: "", price: "", url: "", notes: "", image_url: "", linked_todo_list_id: "" };

export const ShopItemDialog = ({ state, todos, shopLists, onClose, onSaved, onMoved }) => {
  const [form, setForm] = useState(EMPTY);
  const [target, setTarget] = useState("");
  useEffect(() => {
    const it = state?.item;
    setTarget(state?.list.id || "");
    setForm(it ? { name: it.name, price: it.price ?? "", url: it.url || "", notes: it.notes || "", image_url: it.image_url || "", linked_todo_list_id: it.linked_todo_list_id || "" } : EMPTY);
  }, [state]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const body = {
      name: form.name.trim(),
      price: form.price === "" ? null : Number(form.price),
      url: withScheme(form.url.trim()) || null,
      notes: form.notes || null,
      image_url: form.image_url.trim() || null,
      linked_todo_list_id: form.linked_todo_list_id || null,
    };
    const base = `/shop-lists/${state.list.id}/items`;
    let r;
    try {
      r = state.item ? await api.patch(`${base}/${state.item.id}`, body) : await api.post(base, body);
    } catch (err) {
      return toast.error(formatApiError(err).replace(/^Value error, /, ""));
    }
    onSaved(r.data);
    if (state.item && target !== state.list.id) {
      await api.post(`${base}/${state.item.id}/move`, { target_list_id: target });
      onMoved();
    }
    toast.success(state.item ? "Item updated" : "Item added");
    onClose();
  };

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-t-bg border-t-line rounded-2xl sm:max-w-lg" data-testid="shop-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl font-semibold">{state?.item ? "Edit item" : `Add to ${state?.list.name || ""}`}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Item"><input data-testid="shop-item-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="e.g. Linen curtains" autoFocus /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Price (AUD)"><input data-testid="shop-item-price-input" type="number" step="0.01" min="0" className={inputCls} value={form.price} onChange={set("price")} placeholder="0.00" /></Field>
            <Field label="Link to to-do list">
              <select data-testid="shop-item-link-select" className={inputCls} value={form.linked_todo_list_id} onChange={set("linked_todo_list_id")}>
                <option value="">Not linked</option>
                {todos.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>
          </div>
          {state?.item && (
            <Field label="List">
              <select data-testid="shop-item-move-select" className={inputCls} value={target} onChange={(e) => setTarget(e.target.value)}>
                {shopLists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.kind === "wishlist" ? "Wishlist" : "To-Buy"})</option>)}
              </select>
            </Field>
          )}
          <Field label="Store URL"><input data-testid="shop-item-url-input" className={inputCls} value={form.url} onChange={set("url")} placeholder="https://" /></Field>
          <Field label="Picture link"><input data-testid="shop-item-image-input" className={inputCls} value={form.image_url} onChange={set("image_url")} placeholder="https://... (optional)" /></Field>
          <Field label="Notes"><input data-testid="shop-item-notes-input" className={inputCls} value={form.notes} onChange={set("notes")} placeholder="Size, colour, brand..." /></Field>
          <button type="submit" data-testid="shop-item-save-button" className={`${btnPrimary} w-full`}>Save item</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
